// API Configuration Service
import axios from 'axios';

class ConfigService {
  constructor() {
    this.baseURL = 'https://cgb-backend.carl-g-bisaillon.workers.dev/api/config';
  }

  // Get current configuration status
  async getConfigStatus() {
    try {
      const response = await axios.get(`${this.baseURL}/status`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to get config status:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to get configuration status'
      };
    }
  }

  // Test API connection for a specific platform
  async testPlatform(platform, credentials) {
    try {
      const response = await axios.post(`${this.baseURL}/test/${platform}`, credentials);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error(`Failed to test ${platform}:`, error);
      return {
        success: false,
        error: error.response?.data?.message || `Failed to test ${platform} connection`
      };
    }
  }

  // Save configuration to backend (this would typically update environment variables)
  async saveConfiguration(platform, credentials) {
    try {
      // For now, we'll store in localStorage as a fallback
      // In a production app, this would be sent to the backend to update environment variables
      const existingConfig = JSON.parse(localStorage.getItem('apiConfig') || '{}');
      existingConfig[platform] = credentials;
      localStorage.setItem('apiConfig', JSON.stringify(existingConfig));

      // In a real implementation, you would send this to the backend:
      // const response = await axios.post(`${this.baseURL}/save`, { platform, credentials });
      
      return {
        success: true,
        message: `${platform} configuration saved successfully`
      };
    } catch (error) {
      console.error(`Failed to save ${platform} configuration:`, error);
      return {
        success: false,
        error: error.message || `Failed to save ${platform} configuration`
      };
    }
  }

  // Load configuration from backend
  async loadConfiguration() {
    try {
      // For now, load from localStorage
      // In a real implementation, this would come from the backend
      const config = JSON.parse(localStorage.getItem('apiConfig') || '{}');
      
      return {
        success: true,
        data: config
      };
    } catch (error) {
      console.error('Failed to load configuration:', error);
      return {
        success: false,
        error: error.message || 'Failed to load configuration'
      };
    }
  }

  // Get configuration for a specific platform
  getPlatformConfig(platform) {
    try {
      const config = JSON.parse(localStorage.getItem('apiConfig') || '{}');
      return config[platform] || {};
    } catch (error) {
      console.error(`Failed to get ${platform} configuration:`, error);
      return {};
    }
  }

  // Clear configuration for a platform
  clearPlatformConfig(platform) {
    try {
      const config = JSON.parse(localStorage.getItem('apiConfig') || '{}');
      delete config[platform];
      localStorage.setItem('apiConfig', JSON.stringify(config));
      return { success: true };
    } catch (error) {
      console.error(`Failed to clear ${platform} configuration:`, error);
      return { success: false, error: error.message };
    }
  }

  // Check if a platform is configured
  isPlatformConfigured(platform) {
    const config = this.getPlatformConfig(platform);
    return Object.keys(config).length > 0 && Object.values(config).some(value => value && value.trim() !== '');
  }

  // Get all configured platforms
  async getConfiguredPlatforms() {
    try {
      const response = await this.getConfigStatus();
      if (response.success) {
        return Object.keys(response.data.config).filter(platform => 
          response.data.config[platform].configured
        );
      }
      return [];
    } catch (error) {
      console.error('Failed to get configured platforms:', error);
      return [];
    }
  }
}

export const configService = new ConfigService();
export default configService;
